import type { TileRef } from "../document/types.ts";
import type { BrushEntry, BrushSet } from "./brush-set-file.ts";
import { frameKey, probe, type FrameLookups } from "./scatter-entries.ts";

/**
 * Known ground cover (ADR-123's amendment of 2026-10-13, A2): the frames that no brush treats as standing in the
 * way. A frame is cover by what the author's sets say of it, not by a mark on the placed prop, so nothing in a
 * document, a wire message or a compiled world records it.
 */

/**
 * The frames that are ground cover in any usable frame entry of any of the sets given, by `frameKey` (flips ignored).
 * An entry says so itself with `groundCover`; one that says nothing is cover when its frame blocks no cell, read from
 * the editor's footprint for it now, because a footprint lives in the library and can change. A frame that is
 * cover in one set and explicitly solid in another is cover. An entry whose frame the world cannot size is not
 * usable and adds nothing, nor does a template entry (never cover in this build). The caller gives only the sets
 * it could read, so a set that is broken or made by a newer editor adds nothing.
 *
 * @param sets The sets the tab can read.
 * @param lookups The editor's frame sizes and footprints.
 * @returns The keys of the frames that are cover.
 */
export function knownCover(sets: readonly BrushSet[], lookups: FrameLookups): Set<string> {
  const cover = new Set<string>();
  for (const set of sets) {
    for (const entry of set.entries) {
      if (entry.kind === "frame" && lookups.frameSize(entry.frame) !== undefined && entryCover(entry, lookups)) {
        cover.add(frameKey(entry.frame));
      }
    }
  }
  return cover;
}

/**
 * Whether an entry is ground cover as its set says: a template entry never is, an explicit `groundCover` is the
 * choice, and an entry that says nothing is cover when its frame blocks no cell. The panel shows this state and
 * `knownCover` adds the usable entries that have it.
 *
 * @param entry The entry.
 * @param lookups The editor's footprints.
 * @returns Whether the entry is cover.
 */
export function entryCover(entry: BrushEntry, lookups: FrameLookups): boolean {
  if (entry.kind !== "frame") {
    return false;
  }
  if (entry.groundCover !== undefined) {
    return entry.groundCover;
  }
  const footprint = lookups.footprintOf(probe(entry.frame));
  return footprint === undefined || footprint.runs.length === 0;
}

/**
 * The names of the sets that make a frame ground cover, in the order given: a set with a usable frame entry of it
 * that is cover. The panel says "Also ground cover in" these on an entry that is not cover itself.
 *
 * @param sets The sets to read, named; the caller leaves out the set being edited.
 * @param frame The frame.
 * @param lookups The editor's frame sizes and footprints.
 * @returns The set names.
 */
export function coverSetsOf(sets: readonly { readonly name: string; readonly entries: readonly BrushEntry[] }[], frame: TileRef, lookups: FrameLookups): string[] {
  if (lookups.frameSize(frame) === undefined) {
    return [];
  }
  const key = frameKey(frame);
  return sets.filter((set) => set.entries.some((entry) => entry.kind === "frame" && frameKey(entry.frame) === key && entryCover(entry, lookups))).map((set) => set.name);
}
